import DateTimePicker from '@react-native-community/datetimepicker';
import type { DateTimePickerEvent } from '@react-native-community/datetimepicker';
import { CalendarDays } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, CSSProperties } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { MINIMUM_ACCOUNT_AGE } from '@/src/utils/age';

type DateOfBirthPickerProps = {
  error?: string;
  label: string;
  onBlur?: () => void;
  onChangeText: (value: string) => void;
  value: string;
};

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
const MAX_BIRTH_YEAR_LOOKBACK = 120;
const MONTH_OPTIONS = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
] as const;

type WebDateParts = {
  day: string;
  month: string;
  year: string;
};

const emptyWebDateParts: WebDateParts = {
  day: '',
  month: '',
  year: '',
};

function formatDateValue(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseDateValue(value: string) {
  if (!isoDatePattern.test(value)) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function webDatePartsFromDate(date: Date): WebDateParts {
  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    year: String(date.getFullYear()),
  };
}

function webDatePartsFromValue(value: string) {
  const date = parseDateValue(value);

  return date ? webDatePartsFromDate(date) : null;
}

function getDaysInMonth(month: string, year: string) {
  const monthNumber = Number(month);

  if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
    return 31;
  }

  const yearNumber = Number(year) || 2000;

  return new Date(yearNumber, monthNumber, 0).getDate();
}

function clampWebDateParts(parts: WebDateParts) {
  if (!parts.day) {
    return parts;
  }

  const maxDay = getDaysInMonth(parts.month, parts.year);
  const dayNumber = Number(parts.day);

  if (dayNumber > maxDay) {
    return {
      ...parts,
      day: String(maxDay).padStart(2, '0'),
    };
  }

  return parts;
}

function webDateValueFromParts(parts: WebDateParts) {
  if (!parts.month || !parts.day || !parts.year) {
    return '';
  }

  return `${parts.year}-${parts.month}-${parts.day}`;
}

function defaultPickerDate() {
  const date = new Date();

  date.setFullYear(date.getFullYear() - 18);

  return date;
}

export function DateOfBirthPicker({
  error,
  label,
  onBlur,
  onChangeText,
  value,
}: DateOfBirthPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const today = useMemo(() => new Date(), []);
  const [webDateParts, setWebDateParts] = useState<WebDateParts>(() => {
    return webDatePartsFromValue(value) ?? emptyWebDateParts;
  });
  const previousValueRef = useRef(value);
  const selectedDate = parseDateValue(value);
  const pickerDate = selectedDate ?? defaultPickerDate();
  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : 'Select your date of birth';
  const maxBirthYear = today.getFullYear() - MINIMUM_ACCOUNT_AGE;
  const minBirthYear = today.getFullYear() - MAX_BIRTH_YEAR_LOOKBACK;
  const yearOptions = useMemo(
    () =>
      Array.from({ length: maxBirthYear - minBirthYear + 1 }, (_, index) =>
        String(maxBirthYear - index),
      ),
    [maxBirthYear, minBirthYear],
  );
  const dayOptions = useMemo(
    () =>
      Array.from(
        { length: getDaysInMonth(webDateParts.month, webDateParts.year) },
        (_, index) => String(index + 1).padStart(2, '0'),
      ),
    [webDateParts.month, webDateParts.year],
  );

  useEffect(() => {
    const syncedParts = webDatePartsFromValue(value);

    if (syncedParts) {
      setWebDateParts(syncedParts);
    } else if (value === '' && previousValueRef.current !== '') {
      setWebDateParts(emptyWebDateParts);
    }

    previousValueRef.current = value;
  }, [value]);

  function getWebSelectEventValue(event?: ChangeEvent<HTMLSelectElement>) {
    if (!event) {
      return '';
    }

    const currentTargetValue = event.currentTarget?.value;

    if (typeof currentTargetValue === 'string') {
      return currentTargetValue;
    }

    const targetValue = event.target?.value;

    return typeof targetValue === 'string' ? targetValue : '';
  }

  function updateWebDatePart(
    part: keyof WebDateParts,
    event: ChangeEvent<HTMLSelectElement>,
  ) {
    const nextParts = clampWebDateParts({
      ...webDateParts,
      [part]: getWebSelectEventValue(event),
    });

    setWebDateParts(nextParts);
    onChangeText(webDateValueFromParts(nextParts));
  }

  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <Text style={styles.label}>{label}</Text>
        <div style={webDateRowStyle}>
          <select
            aria-label={`${label} month`}
            onBlur={onBlur}
            onChange={(event) => updateWebDatePart('month', event)}
            style={{
              ...webSelectStyle,
              ...webMonthSelectStyle,
              ...(error ? webInputErrorStyle : null),
            }}
            value={webDateParts.month}
          >
            <option value="">Month</option>
            {MONTH_OPTIONS.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            aria-label={`${label} day`}
            onBlur={onBlur}
            onChange={(event) => updateWebDatePart('day', event)}
            style={{
              ...webSelectStyle,
              ...webDaySelectStyle,
              ...(error ? webInputErrorStyle : null),
            }}
            value={webDateParts.day}
          >
            <option value="">Day</option>
            {dayOptions.map((day) => (
              <option key={day} value={day}>
                {Number(day)}
              </option>
            ))}
          </select>
          <select
            aria-label={`${label} year`}
            onBlur={onBlur}
            onChange={(event) => updateWebDatePart('year', event)}
            style={{
              ...webSelectStyle,
              ...webYearSelectStyle,
              ...(error ? webInputErrorStyle : null),
            }}
            value={webDateParts.year}
          >
            <option value="">Year</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        {error ? <Text style={styles.error}>{error}</Text> : null}
      </View>
    );
  }

  function handleChange(event: DateTimePickerEvent, date?: Date) {
    if (Platform.OS === 'android') {
      setShowPicker(false);
    }

    if (event.type === 'dismissed') {
      onBlur?.();
      return;
    }

    if (date) {
      onChangeText(formatDateValue(date));
      onBlur?.();
    }
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <Pressable
        accessibilityRole="button"
        onPress={() => setShowPicker(true)}
        style={[styles.inputButton, error ? styles.inputError : null]}
      >
        <Text
          style={[
            styles.inputButtonText,
            selectedDate ? null : styles.placeholderText,
          ]}
        >
          {displayValue}
        </Text>
        <CalendarDays color={colors.mutedText} size={20} strokeWidth={2.4} />
      </Pressable>
      {showPicker ? (
        <DateTimePicker
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          maximumDate={today}
          mode="date"
          value={pickerDate}
          onChange={handleChange}
        />
      ) : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
    </View>
  );
}

const webInputStyle: CSSProperties = {
  backgroundColor: colors.inputBackground,
  borderColor: colors.border,
  borderRadius: 8,
  borderStyle: 'solid',
  borderWidth: 1,
  boxSizing: 'border-box',
  color: colors.text,
  fontFamily: 'inherit',
  fontSize: 16,
  minHeight: 48,
  outlineColor: colors.primary,
  padding: `${spacing.sm}px ${spacing.md}px`,
  width: '100%',
};

const webSelectStyle: CSSProperties = {
  ...webInputStyle,
  appearance: 'auto',
};

const webDateRowStyle: CSSProperties = {
  display: 'flex',
  gap: spacing.sm,
  width: '100%',
};

const webMonthSelectStyle: CSSProperties = {
  flex: '1 1 44%',
  minWidth: 0,
};

const webDaySelectStyle: CSSProperties = {
  flex: '0 1 24%',
  minWidth: 84,
};

const webYearSelectStyle: CSSProperties = {
  flex: '0 1 32%',
  minWidth: 104,
};

const webInputErrorStyle: CSSProperties = {
  borderColor: colors.danger,
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.xs,
  },
  error: {
    color: colors.danger,
    fontSize: 13,
  },
  inputButton: {
    alignItems: 'center',
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  inputButtonText: {
    color: colors.text,
    flex: 1,
    fontSize: 16,
  },
  inputError: {
    borderColor: colors.danger,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  placeholderText: {
    color: colors.mutedText,
  },
});
